/**
 * Where a signed-in traveller gives the consents the backend asks for.
 *
 * Two jobs, mounted once in App:
 *
 * - An account that has not answered the current consent texts - every
 *   account created before 2026-10, and anyone after a text changes - gets a
 *   one-time screen with the same boxes as sign-up. Until it is answered the
 *   backend writes no new health data and sends nothing to the AI, so asking
 *   up front beats a refusal in the middle of a form. The legal pages stay
 *   readable behind it, since the boxes link to them.
 * - A request refused with 403 CONSENT_REQUIRED (a traveller who declined and
 *   now tries to store something) opens a small dialog offering that one
 *   consent in place.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useI18nStore } from "@/stores/i18nStore";
import { CONSENT_REQUIRED_EVENT } from "@/utils/consentRequired";
import {
  CORE_CONSENTS_QUERY,
  type CoreConsent,
  type CoreDecisions,
  getCoreConsents,
  saveCoreConsents,
} from "@/utils/coreConsents";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ConsentChoices from "./ConsentChoices";

/**
 * Pages the one-time screen never covers: the legal pages its boxes link to,
 * and the pages someone opens for another person or from a mail - a doctor
 * who is signed in to their own account must reach a scanned emergency card.
 */
const UNGATED_PREFIXES = [
  "/terms",
  "/privacy",
  "/imprint",
  "/contact",
  "/security",
  "/emergency/",
  "/card/share/",
  "/checkin/",
  "/med-access/",
  "/verify-email",
  "/reset-password",
  "/payment-success",
];

export function isGatedPath(pathname: string): boolean {
  return !UNGATED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function useSaveConsents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveCoreConsents,
    onSuccess: (state) => queryClient.setQueryData(CORE_CONSENTS_QUERY, state),
  });
}

function DecisionScreen({ initial }: { initial: CoreDecisions }) {
  const { t } = useI18nStore();
  const [choices, setChoices] = useState<CoreDecisions>(initial);
  const [error, setError] = useState("");
  const save = useSaveConsents();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!choices.TERMS_OF_SERVICE) {
      setError(t.acceptTermsRequired);
      return;
    }
    setError("");
    // Every box is an answer: an unticked one is recorded as declined, so the
    // traveller is not asked again until the text changes.
    save.mutate(
      {
        TERMS_OF_SERVICE: true,
        HEALTH_DATA: choices.HEALTH_DATA === true,
        AI_PROCESSING: choices.AI_PROCESSING === true,
        MARKETING_COMMUNICATIONS: choices.MARKETING_COMMUNICATIONS === true,
      },
      { onError: () => setError(t.consentSaveFailed) },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-gate-title"
      className="fixed inset-0 z-[60] overflow-y-auto bg-ground px-4 pb-12 pt-10 text-ink"
    >
      <form onSubmit={submit} className="mx-auto max-w-[520px]">
        <ShieldCheck
          className="h-10 w-10 text-primary-600"
          aria-hidden="true"
        />
        <h1
          id="consent-gate-title"
          className="mt-4 font-display text-[26px] font-extrabold leading-tight"
        >
          {t.consentGateTitle}
        </h1>
        <p className="mt-2 text-[15px] leading-normal text-slate-600">
          {t.consentGateLead}
        </p>
        <div className="mt-6 rounded-[22px] bg-card p-5">
          <ConsentChoices value={choices} onChange={setChoices} />
        </div>
        {error && (
          <p role="alert" className="mt-4 text-sm font-medium text-red-700">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={save.isPending}
          className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-ink px-4 text-[15px] font-bold text-white disabled:opacity-60"
        >
          {t.consentGateSave}
        </button>
      </form>
    </div>
  );
}

function AskInPlace({
  type,
  onClose,
}: {
  type: CoreConsent;
  onClose: () => void;
}) {
  const { t } = useI18nStore();
  const save = useSaveConsents();
  const [failed, setFailed] = useState(false);
  const ai = type === "AI_PROCESSING";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-primary-600" />
            <DialogTitle>
              {ai ? t.consentNeededAiTitle : t.consentNeededHealthTitle}
            </DialogTitle>
          </div>
          <DialogDescription>
            {ai ? t.consentAi : t.consentHealth}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500">{t.consentWithdrawHint}</p>
        {failed && (
          <p role="alert" className="text-sm font-medium text-red-700">
            {t.consentSaveFailed}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            {t.consentNotNow}
          </button>
          <button
            type="button"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(
                { [type]: true },
                { onSuccess: onClose, onError: () => setFailed(true) },
              )
            }
            className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {t.consentAllow}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ConsentGate: React.FC = () => {
  const { pathname } = useLocation();
  const [asked, setAsked] = useState<CoreConsent | null>(null);
  const { data } = useQuery({
    queryKey: CORE_CONSENTS_QUERY,
    queryFn: getCoreConsents,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    const onRequired = (event: Event) =>
      setAsked((event as CustomEvent<CoreConsent>).detail);
    window.addEventListener(CONSENT_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(CONSENT_REQUIRED_EVENT, onRequired);
  }, []);

  if (data?.needsDecision && isGatedPath(pathname)) {
    // Keep what the traveller already granted; ask the rest fresh.
    const initial: CoreDecisions = {};
    for (const [type, entry] of Object.entries(data.consents)) {
      if (entry.decision === "granted" && entry.current)
        initial[type as CoreConsent] = true;
    }
    return <DecisionScreen initial={initial} />;
  }

  return asked ? (
    <AskInPlace type={asked} onClose={() => setAsked(null)} />
  ) : null;
};

export default ConsentGate;
